import type {
  FinanceSummary, FinanceRecord, FinanceOrder, CreatorSupportEnrollment,
  AdminComment,
  AdminPost,
  AdminSession,
  AdminUser,
  AuditLog,
  DashboardResponse,
  Paginated,
  ReportDetail,
  ReportedMessageDetail,
  ReportSummary,
  VoiceReview,
} from './types';

const DEFAULT_API_BASE = 'https://api.flames-up.com/api';
const DEFAULT_TIMEOUT_MS = 12000;

export const ADMIN_TOKEN_KEY = 'captro.admin.token';
export const ADMIN_REFRESH_TOKEN_KEY = 'captro.admin.refresh-token';

export const API_BASE = (import.meta.env.VITE_CAPTRO_API_BASE || DEFAULT_API_BASE).replace(/\/+$/, '');
const API_TIMEOUT_MS = Number(import.meta.env.VITE_CAPTRO_API_TIMEOUT_MS || DEFAULT_TIMEOUT_MS);

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function requestId() {
  return globalThis.crypto?.randomUUID?.() || `captro-admin-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function fetchWithTimeout(url: string, options: RequestInit = {}) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  try {
    return await fetch(url, {
      ...options,
      credentials: 'omit',
      signal: controller.signal,
    });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') {
      throw new ApiError('Captro API took too long to respond. Please retry.', 408);
    }
    throw new ApiError('Could not reach Captro API. Check your connection and try again.', 0);
  } finally {
    window.clearTimeout(timeout);
  }
}

async function readPayload(response: Response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError('Captro API returned an unexpected response. Please retry.', 502);
  }
}

async function refreshAdminSession(refreshToken: string) {
  const response = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Request-ID': requestId() },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  const payload = await readPayload(response);
  if (!response.ok) throw new ApiError(payload?.detail || 'Session refresh failed', response.status);
  return payload as { access_token: string; refresh_token?: string };
}

let refreshInFlight: {owner:string; promise:ReturnType<typeof refreshAdminSession>} | undefined;
let sessionEpoch=0;
export function invalidateAdminSession(){sessionEpoch++;refreshInFlight=undefined;}
export async function request<T>(path: string, token: string, options: RequestInit = {}): Promise<T> {
  const epoch=sessionEpoch;
  const owner=sessionStorage.getItem(ADMIN_REFRESH_TOKEN_KEY) || '';
  const access=sessionStorage.getItem(ADMIN_TOKEN_KEY) || token;
  const identity=requestId();
  const makeHeaders = (accessToken: string) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'X-Request-ID': identity,
    ...(options.headers || {}),
  });

  let response = await fetchWithTimeout(`${API_BASE}${path}`, {
    ...options,
    headers: makeHeaders(access),
  });

  if (response.status === 401 && !path.endsWith('/auth/refresh')) {
    const refreshToken = sessionStorage.getItem(ADMIN_REFRESH_TOKEN_KEY) || '';
    if (refreshToken) {
      try {
        if(!refreshInFlight || refreshInFlight.owner!==refreshToken) {
          const promise=refreshAdminSession(refreshToken);
          refreshInFlight={owner:refreshToken,promise};
          void promise.finally(()=>{if(refreshInFlight?.promise===promise)refreshInFlight=undefined;}).catch(()=>{});
        }
        const refreshed = await refreshInFlight.promise;
        if(sessionEpoch!==epoch) throw new ApiError('Account changed. Please reload.',409);
        if(!refreshed.access_token)throw new ApiError('Invalid refresh response. Please retry.',502);
        const nextAccessToken = refreshed.access_token || token;
        const nextRefreshToken = refreshed.refresh_token || refreshToken;
        sessionStorage.setItem(ADMIN_TOKEN_KEY, nextAccessToken);
        sessionStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, nextRefreshToken);
        response = await fetchWithTimeout(`${API_BASE}${path}`, {
          ...options,
          headers: makeHeaders(nextAccessToken),
        });
      } catch (error) {
        if(error instanceof ApiError && error.status===401 && sessionStorage.getItem(ADMIN_REFRESH_TOKEN_KEY)===owner) {
          sessionStorage.removeItem(ADMIN_TOKEN_KEY);
          sessionStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
        }
        throw error;
      }
    }
  }

  const payload = await readPayload(response);
  if(sessionEpoch!==epoch)throw new ApiError('Account changed. Please reload.',409);
  if (!response.ok) {
    throw new ApiError(payload?.detail || 'Request failed', response.status);
  }
  return payload as T;
}

export async function login(email: string, password: string) {
  const response = await fetchWithTimeout(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Request-ID': requestId() },
    body: JSON.stringify({ email, password }),
  });
  const payload = await readPayload(response);
  if (!response.ok) throw new ApiError(payload?.detail || 'Login failed', response.status);
  return payload as { access_token: string; refresh_token?: string };
}

export const AdminApi = {
  financePools: (token:string)=>request<FinanceSummary>('/admin/finance/pools',token),
  paymentRecovery: (token:string)=>request<import('./RecoveryPanel').RecoveryQueue>('/admin/finance/recovery',token),
  paymentRecoveryCase: (token:string,id:string)=>request<import('./RecoveryPanel').RecoveryCase>('/admin/finance/recovery/'+encodeURIComponent(id),token),
  resolvePaymentRecovery: (token:string,id:string,body:{decision:'fulfill'|'refund';note:string})=>
    request<{status:string}>('/admin/finance/recovery/'+encodeURIComponent(id)+'/resolve',token,{method:'POST',body:JSON.stringify(body)}),
  financeRecords: (token:string,query:string)=>request<{items:FinanceRecord[];nextOffset:number|null}>('/admin/finance/records'+query,token),
  financeOrder: (token:string,id:string,offset=0)=>request<FinanceOrder>('/admin/finance/orders/'+encodeURIComponent(id)+'?offset='+offset,token),
  releaseEarning: (token:string,id:string,body:{releaseAfter:string;note:string})=>request<{status:string;transferId?:string}>('/admin/commerce/purchases/'+encodeURIComponent(id)+'/release',token,{method:'POST',body:JSON.stringify(body)}),
  creatorSupportEnrollment: (token:string,id:string)=>request<CreatorSupportEnrollment>('/admin/creator-support/enrollments/'+encodeURIComponent(id),token),
  setCreatorSupportEnrollment: (token:string,id:string,body:{enabled:boolean;expectedReviewedAt:string|null;reason:string})=>request<CreatorSupportEnrollment>('/admin/creator-support/enrollments/'+encodeURIComponent(id),token,{method:'POST',body:JSON.stringify(body)}),
  me: (token: string) => request<AdminSession>('/admin/me', token),
  health: (token: string) => request<Record<string, string>>('/admin/health', token),
  dashboard: (token: string) => request<DashboardResponse>('/admin/dashboard', token),
  reports: (token: string, query = '') => request<Paginated<ReportSummary>>(`/admin/reports${query}`, token),
  report: (token: string, id: string) => request<{ report: ReportDetail }>(`/admin/reports/${encodeURIComponent(id)}`, token),
  reportStatus: (token: string, id: string, body: Record<string, unknown>) =>
    request<{ report: ReportDetail }>(`/admin/reports/${encodeURIComponent(id)}/status`, token, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reportAction: (token: string, id: string, body: Record<string, unknown>) =>
    request<{ report: ReportDetail }>(`/admin/reports/${encodeURIComponent(id)}/action`, token, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reportNote: (token: string, id: string, note: string) =>
    request<{ added: boolean }>(`/admin/reports/${encodeURIComponent(id)}/note`, token, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  users: (token: string, query = '') => request<Paginated<AdminUser>>(`/admin/users${query}`, token),
  user: (token: string, id: string) => request<{ user: AdminUser; restrictions: unknown[]; actions: AuditLog[]; recent_posts: AdminPost[] }>(`/admin/users/${encodeURIComponent(id)}`, token),
  warnUser: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/users/${encodeURIComponent(id)}/warn`, token, { method: 'POST', body: JSON.stringify(body) }),
  restrictUser: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/users/${encodeURIComponent(id)}/restrict`, token, { method: 'POST', body: JSON.stringify(body) }),
  suspendUser: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/users/${encodeURIComponent(id)}/suspend`, token, { method: 'POST', body: JSON.stringify(body) }),
  banUser: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/users/${encodeURIComponent(id)}/ban`, token, { method: 'POST', body: JSON.stringify(body) }),
  unbanUser: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/users/${encodeURIComponent(id)}/unban`, token, { method: 'POST', body: JSON.stringify(body) }),
  forceUsername: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/users/${encodeURIComponent(id)}/force-username-change`, token, { method: 'POST', body: JSON.stringify(body) }),
  posts: (token: string, query = '') => request<Paginated<AdminPost>>(`/admin/posts${query}`, token),
  post: (token: string, id: string) => request<{ post: AdminPost; actions?: AuditLog[] }>(`/admin/posts/${encodeURIComponent(id)}`, token),
  removePost: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/posts/${encodeURIComponent(id)}/remove`, token, { method: 'POST', body: JSON.stringify(body) }),
  restorePost: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/posts/${encodeURIComponent(id)}/restore`, token, { method: 'POST', body: JSON.stringify(body) }),
  markPostSafe: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/posts/${encodeURIComponent(id)}/mark-safe`, token, { method: 'POST', body: JSON.stringify(body) }),
  removeFromDiscover: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/posts/${encodeURIComponent(id)}/remove-from-discover`, token, { method: 'POST', body: JSON.stringify(body) }),
  clearPostLocation: (token: string, id: string, body: Record<string, unknown>) =>
    request<{ post: AdminPost }>(`/admin/posts/${encodeURIComponent(id)}/location/clear`, token, { method: 'POST', body: JSON.stringify(body) }),
  changePostCategory: (token: string, id: string, body: Record<string, unknown>) =>
    request<{ post: AdminPost }>(`/admin/posts/${encodeURIComponent(id)}/category`, token, { method: 'POST', body: JSON.stringify(body) }),
  comments: (token: string, query = '') => request<Paginated<AdminComment>>(`/admin/comments${query}`, token),
  removeComment: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/comments/${encodeURIComponent(id)}/remove`, token, { method: 'POST', body: JSON.stringify(body) }),
  restoreComment: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/admin/comments/${encodeURIComponent(id)}/restore`, token, { method: 'POST', body: JSON.stringify(body) }),
  reportedMessages: (token: string, query = '') => request<Paginated<ReportSummary>>(`/admin/messages/reported${query}`, token),
  reportedMessage: (token: string, id: string) => request<ReportedMessageDetail>(`/admin/messages/reported/${encodeURIComponent(id)}`, token),
  reportedMessageAction: (token: string, id: string, body: Record<string, unknown>) =>
    request<{ report: ReportDetail }>(`/admin/messages/reported/${encodeURIComponent(id)}/action`, token, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  auditLogs: (token: string, query = '') => request<Paginated<AuditLog>>(`/admin/audit-logs${query}`, token),
  voiceReviews: (token: string) => request<VoiceReview[]>('/voice/admin/review', token),
  decideVoice: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/voice/admin/${encodeURIComponent(id)}/decision`, token, { method: 'POST', body: JSON.stringify(body) }),
  voiceAudio: async (token: string, id: string) => {
    const response = await fetchWithTimeout(`${API_BASE}/voice/${encodeURIComponent(id)}/playback`, {
      headers: { Authorization: `Bearer ${token}`, 'X-Request-ID': requestId() },
    });
    if (!response.ok) throw new ApiError('Recording is unavailable.', response.status);
    return response.blob();
  },
};
